import { Box, Typography, useTheme, CircularProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const Invoices = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const location = useLocation();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const query = new URLSearchParams(location.search).get("q")?.trim() || "";

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        const response = await axios.get(
          `${BASE_URL}/api/admin/dashboard-stats`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const formatted = response.data.recentTransactions.map((tx, index) => ({
          id: tx._id || index,
          name: tx.payerName || "N/A",
          phone: tx.phoneNumber || "N/A",
          email: tx.payerEmail || "N/A",
          cost: tx.amount,
          book: tx.bookTitle || "N/A",
          txId: tx.transactionId || "N/A",
          date: new Date(tx.createdAt).toLocaleDateString(),
        }));

        setInvoices(formatted);
      } catch (error) {
        console.error("Failed to fetch invoices:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  const columns = [
    {
      field: "txId",
      headerName: "Transaction ID",
      flex: 1,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
    },
    {
      field: "book",
      headerName: "Book",
      flex: 1,
    },
    {
      field: "cost",
      headerName: "Amount (KES)",
      flex: 1,
      renderCell: (params) => (
        <Typography color={colors.greenAccent[500]}>
          KES {params.row.cost}
        </Typography>
      ),
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
    },
  ];

  const filteredInvoices = useMemo(() => {
    if (!query) return invoices;
    const q = query.toLowerCase();
    return invoices.filter((invoice) =>
      [
        invoice.txId,
        invoice.name,
        invoice.phone,
        invoice.email,
        invoice.book,
        invoice.date,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q)),
    );
  }, [invoices, query]);

  return (
    <Box m="20px">
      <Header title="INVOICES" subtitle="List of Completed Transactions" />
      {query && (
        <Typography color={colors.grey[300]} mt="8px">
          Search results for "{query}" ({filteredInvoices.length})
        </Typography>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt="100px">
          <CircularProgress />
        </Box>
      ) : (
        <Box
          m="40px 0 0 0"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .name-column--cell": { color: colors.greenAccent[300] },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: colors.blueAccent[700],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: colors.primary[400],
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "none",
              backgroundColor: colors.blueAccent[700],
            },
            "& .MuiCheckbox-root": {
              color: `${colors.greenAccent[200]} !important`,
            },
          }}
        >
          <DataGrid checkboxSelection rows={filteredInvoices} columns={columns} />
        </Box>
      )}
    </Box>
  );
};

export default Invoices;
